/**
 * Frequency measures of disease: risk, rate and prevalence.
 *
 * - Cumulative incidence (risk, attack rate): new cases ÷ people at risk at the start.
 *   A proportion, so a Wilson interval.
 * - Incidence rate: new cases ÷ person-time at risk. The count is treated as Poisson, so
 *   the interval is the exact chi-square one divided by the person-time.
 * - Prevalence (point or period): existing cases ÷ population. A proportion again.
 *
 * Results are fractions; the calculator multiplies by its chosen base (per 100, 1,000,
 * 10,000 or 100,000).
 */

import {
  type Interval,
  type ProportionEstimate,
  poissonCountInterval,
  wilsonInterval,
} from "@/lib/tool-kit/stats-research/proportions";

export interface RateEstimate extends Interval {
  value: number;
  cases: number;
  personTime: number;
}

/** Cases per unit of person-time, with the exact Poisson interval. */
export function incidenceRate(
  cases: number,
  personTime: number,
  level = 0.95
): RateEstimate | null {
  if (!(personTime > 0) || cases < 0 || !Number.isInteger(cases)) {
    return null;
  }
  const count = poissonCountInterval(cases, level);
  return {
    value: cases / personTime,
    cases,
    personTime,
    lower: count.lower / personTime,
    upper: count.upper / personTime,
  };
}

/** Cumulative incidence or prevalence: a count out of a population, Wilson interval. */
export function proportionOf(
  cases: number,
  population: number,
  level = 0.95
): ProportionEstimate | null {
  if (!(Number.isInteger(cases) && Number.isInteger(population))) {
    return null;
  }
  return wilsonInterval(cases, population, level);
}

/** The bases the calculator offers: per 100 (%), 1,000, 10,000 and 100,000. */
export const RATE_BASES = [100, 1000, 10_000, 100_000] as const;

export type RateBase = (typeof RATE_BASES)[number];
